import React, { useEffect, useState } from 'react';
import { Header, Wrapper } from '../../general_components/forms/Form';
import { PopupOnClick } from '../../general_components/content_management/reusableItems/itemOnPopup';
import { addPopup } from '../../supplementary_components/store';
import { LoadingIcon } from '../../supplementary_components/loading_icon';
import {
    DATAQ_OPTIONS,
    RESULT_OPTIONS,
    errorMessage,
    formatIsoDate,
    labelFor,
    safetyApi,
    violationSuggestionLabel,
} from './safetyApi';
import styles from './inspections.module.scss';

export function inspectionListLabel(row) {
    if (!row?.inspectionId) return null;
    if (row.inspectionNumber) return row.inspectionNumber;
    if (row.truckNumber && row.truckNumber !== '-') return `${row.truckNumber} Inspection`;
    return 'Inspection';
}

export function openSafetyRecord(kind, id) {
    if (id == null) return;
    addPopup({
        content: SafetyRecordPopup,
        popupData: { kind, id },
        customWidth: '46rem',
        EZCloseAllowed: true,
    });
}

function RecordLink({ text, itemType, itemId }) {
    if (itemId == null || !text || text === '-') return text || '-';
    return <PopupOnClick text={text} itemType={itemType} itemId={itemId} alwaysUnderlined />;
}

function violationText(violation) {
    const custom = (violation?.description || '').trim();
    if (custom) return `${violation.code} ${custom}`;
    return violationSuggestionLabel({
        code: violation.code,
        officialDescription: violation.officialDescription,
        violationGroup: violation.violationGroup,
    });
}

const OUT_OF_SERVICE_LINE = {
    DRIVER: 'Out Of Service Driver',
    EQUIPMENT: 'Out Of Service Equipment',
    BOTH: 'Out Of Service Both',
};

export function ViolationCards({ violations }) {
    if (!violations?.length) return <span className={styles.cleanBadge}>No Violations</span>;
    return violations.map((violation) => {
        const oos = OUT_OF_SERVICE_LINE[violation.outOfService] || '';
        return (
            <span className={styles.violationCard} key={violation.code}>
                <span className={styles.violationBody}>{violationText(violation)}</span>
                {oos ? <span className={styles.oosLine}>({oos})</span> : null}
            </span>
        );
    });
}

function Field({ label, children }) {
    return (
        <div className={styles.recordField}>
            <span>{label}</span>
            <div>{children}</div>
        </div>
    );
}

export function SafetyRecordPopup({ popupData }) {
    const [record, setRecord] = useState(null);
    const [error, setError] = useState('');
    const kind = popupData?.kind;
    const id = popupData?.id;

    useEffect(() => {
        const request = kind === 'citation' ? safetyApi.getCitation(id) : safetyApi.getInspection(id);
        request.then(setRecord).catch((loadError) => setError(errorMessage(loadError)));
    }, [kind, id]);

    const title = kind === 'citation'
        ? `Citation ${record?.ticketNumber || ''}`.trim()
        : `Inspection ${record?.inspectionNumber || (record?.truckNumber ? `${record.truckNumber} Inspection` : '')}`.trim();

    return (
        <div onClick={(event) => event.stopPropagation()}>
            <Wrapper>
                <Header><span>{title}</span></Header>
                <div className={styles.recordBody}>
                    {!record && !error ? <LoadingIcon state height="8rem" /> : null}
                    {error ? <div className="errorMessage">{error}</div> : null}
                    {record && kind === 'inspection' ? <InspectionDetails record={record} /> : null}
                    {record && kind === 'citation' ? <CitationDetails record={record} /> : null}
                </div>
            </Wrapper>
        </div>
    );
}

function InspectionDetails({ record }) {
    return (
        <div className={styles.recordGrid}>
            <Field label="Number">{record.inspectionNumber || '-'}</Field>
            <Field label="Date">{formatIsoDate(record.inspectionDate)}</Field>
            <Field label="Driver"><RecordLink text={record.driverName} itemType="driver" itemId={record.driverId} /></Field>
            <Field label="Truck"><RecordLink text={record.truckNumber} itemType="truck" itemId={record.truckId} /></Field>
            <Field label="Trailer">{record.trailerNumber || '-'}</Field>
            <Field label="State">{record.state || '-'}</Field>
            <Field label="Level">{record.level}</Field>
            <Field label="Severity Weight">{record.severityWeight}</Field>
            <Field label="Citations">
                {record.citations?.length ? record.citations.map((citation) => (
                    <button key={citation.id} type="button" className={styles.recordLink} onClick={() => openSafetyRecord('citation', citation.id)}>
                        {citation.ticketNumber || 'Citation'}
                    </button>
                )) : '-'}
            </Field>
            <Field label="Violations"><span className={styles.violationsCell}><ViolationCards violations={record.violations} /></span></Field>
            <Field label="Notes">{record.notes || '-'}</Field>
        </div>
    );
}

function CitationDetails({ record }) {
    const inspectionLabel = inspectionListLabel(record);
    return (
        <div className={styles.recordGrid}>
            <Field label="Ticket">{record.ticketNumber || '-'}</Field>
            <Field label="Date">{formatIsoDate(record.date)}</Field>
            <Field label="Driver"><RecordLink text={record.driverName} itemType="driver" itemId={record.driverId} /></Field>
            <Field label="Truck"><RecordLink text={record.truckNumber} itemType="truck" itemId={record.truckId} /></Field>
            <Field label="Trailer">{record.trailerNumber || '-'}</Field>
            <Field label="State">{record.state || '-'}</Field>
            <Field label="DataQ">{labelFor(DATAQ_OPTIONS, record.dataQStatus)}</Field>
            <Field label="Result">{labelFor(RESULT_OPTIONS, record.result)}</Field>
            <Field label="Lawyer">{record.lawyerName || '-'}</Field>
            <Field label="Trial Date">{formatIsoDate(record.trialDate)}</Field>
            <Field label="Reason">{record.reason || '-'}</Field>
            <Field label="Notes">{record.notes || '-'}</Field>
            <Field label="Inspection">
                {inspectionLabel ? (
                    <button type="button" className={styles.recordLink} onClick={() => openSafetyRecord('inspection', record.inspectionId)}>
                        {inspectionLabel}
                    </button>
                ) : '-'}
            </Field>
        </div>
    );
}
